import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Download, Upload, Plus, Check, X, FileText, Award, Search, Edit2, Trash2, Link2, Copy, UserCheck, Mic, ChevronUp, ChevronDown, Filter, Mail, Video, Send, RefreshCw, History, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import BuscadorMatriculados from '../../../components/BuscadorMatriculados';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminInscriptosPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [curso, setCurso] = useState(null);
  const [inscriptos, setInscriptos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDisertanteModal, setShowDisertanteModal] = useState(false);
  const [editingInscripto, setEditingInscripto] = useState(null);
  const [emitiendo, setEmitiendo] = useState(null);
  const [editingNota, setEditingNota] = useState(null);
  const [notaValue, setNotaValue] = useState('');
  // Tipo de calificación en el editor: 'numero' | 'aprobado' | 'desaprobado' | 'ninguno'
  const [notaTipo, setNotaTipo] = useState('numero');
  const [linkCopied, setLinkCopied] = useState(false);
  const [reenviando, setReenviando] = useState(null);
  const [jornadas, setJornadas] = useState([]);
  const [linkCopiedJornada, setLinkCopiedJornada] = useState(null);
  const [togglingJornada, setTogglingJornada] = useState(null);
  
  // Modal para enviar link virtual
  const [showLinkVirtualModal, setShowLinkVirtualModal] = useState(false);
  const [linkVirtualForm, setLinkVirtualForm] = useState({
    link: '',
    fecha: '',
    mensaje_adicional: '',
    force_resend: false
  });
  const [enviandoLinkVirtual, setEnviandoLinkVirtual] = useState(false);
  
  // Modal para enviar certificados por email
  const [showEnviarCertificadosModal, setShowEnviarCertificadosModal] = useState(false);
  const [certificadosForm, setCertificadosForm] = useState({
    tipo: 'certificado',
    mensaje_adicional: '',
    force_resend: false
  });
  const [enviandoCertificados, setEnviandoCertificados] = useState(false);
  
  // Modal para enviar certificado individual
  const [showEnviarIndividualModal, setShowEnviarIndividualModal] = useState(false);
  const [inscriptoSeleccionado, setInscriptoSeleccionado] = useState(null);
  const [certificadoIndividualForm, setCertificadoIndividualForm] = useState({
    tipo: 'constancia',
    mensaje_adicional: ''
  });
  const [enviandoIndividual, setEnviandoIndividual] = useState(false);
  
  // Historial de envíos de certificados
  const [showHistorialEnviosModal, setShowHistorialEnviosModal] = useState(false);
  const [historialEnvios, setHistorialEnvios] = useState([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  
  // Filtros y ordenamiento
  const [filtroConfirmacion, setFiltroConfirmacion] = useState('todos'); // todos, confirmados, no_confirmo, cancelados
  const [filtroMatriculado, setFiltroMatriculado] = useState('todos'); // todos, lomas, otros, error_matricula
  const [sortConfig, setSortConfig] = useState({ key: 'apellido', direction: 'asc' });
  const [revalidandoLz, setRevalidandoLz] = useState(false);
  
  // Form for adding/editing inscripto
  const [formInscripto, setFormInscripto] = useState({
    nombre: '', apellido: '', dni: '', telefono: '', formacion_academica: '', matricula: '', distrito: '', email: ''
  });

  // Form for disertante certificate
  const [disertanteForm, setDisertanteForm] = useState({
    nombre: '',
    dni: '',
    rol: 'Disertante'
  });

  const FORMACIONES = [
    'Profesional del Trabajo Social',
    'Estudiante Universitaria de la Carrera de Licenciatura en Trabajo Social',
    'Estudiante Universitaria de la Carrera de Tecnicatura en Minoridad y Familia',
    'Estudiante Terciario Carrera de Trabajo Social',
    'Otros'
  ];

  const ROLES_DISERTANTE = [
    'Disertante',
    'Expositor/a',
    'Panelista',
    'Coordinador/a',
    'Moderador/a',
    'Tallerista'
  ];

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [cursoRes, inscriptosRes, jornadasRes] = await Promise.all([
        axios.get(`${API_URL}/capacitaciones/cursos/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/capacitaciones/admin/cursos/${id}/inscriptos`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/capacitaciones/curso/${id}/jornadas`)
      ]);
      setCurso(cursoRes.data);
      setInscriptos(inscriptosRes.data);
      setJornadas(jornadasRes.data.jornadas || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRevalidarLz = async () => {
    if (!window.confirm('¿Re-verificar a todos los inscriptos contra la BD de matriculades? Útil si actualizaste la BD o sospechás que hay typos en números de matrícula.')) {
      return;
    }
    setRevalidandoLz(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/capacitaciones/admin/cursos/${id}/revalidar-matriculados-lz`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const stats = res.data.stats || {};
      alert(`Re-validación completada.\n\n` +
        `Total inscriptos: ${stats.total ?? 0}\n` +
        `Matriculades LZ (por matrícula): ${stats.lz_matricula ?? 0}\n` +
        `Matriculades LZ (por DNI – posible typo): ${stats.lz_dni ?? 0}\n` +
        `No matriculades LZ: ${stats.no_lz ?? 0}\n\n` +
        `Actualizados: ${stats.actualizados ?? 0}`);
      await fetchData();
    } catch (err) {
      console.error('Error revalidando matriculados LZ:', err);
      alert('Error al re-validar matriculades. ' + (err.response?.data?.detail || ''));
    } finally {
      setRevalidandoLz(false);
    }
  };

  // Toggle asistencia por jornada para un inscripto (grilla)
  const handleToggleAsistenciaJornada = async (inscriptoId, jornadaId) => {
    setTogglingJornada(`${inscriptoId}_${jornadaId}`);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(
        `${API_URL}/capacitaciones/admin/inscriptos/${inscriptoId}/jornadas/${jornadaId}/toggle-asistencia`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local state sin refetch
      setInscriptos(prev => prev.map(i => i.id === inscriptoId ? {
        ...i,
        asistio: res.data.asistio_global,
        asistencias_resumen: {
          ...(i.asistencias_resumen || {}),
          asistencias: res.data.asistencias,
          jornadas_asistidas: Object.values(res.data.asistencias).filter(Boolean).length,
          total_jornadas: jornadas.length,
          porcentaje: jornadas.length
            ? Math.round((Object.values(res.data.asistencias).filter(Boolean).length / jornadas.length) * 1000) / 10
            : 0,
        }
      } : i));
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al actualizar asistencia');
    } finally {
      setTogglingJornada(null);
    }
  };

  // Toggle link de asistencia por jornada
  const handleToggleLinkJornada = async (jornadaId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(
        `${API_URL}/capacitaciones/admin/cursos/${id}/jornadas/${jornadaId}/toggle-asistencia-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJornadas(res.data.jornadas || []);
      // Mantener flag global coherente para el resto de la UI
      const any = (res.data.jornadas || []).some(j => j.asistencia_link_activo);
      setCurso(prev => ({ ...prev, asistencia_link_activo: any }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al actualizar link');
    }
  };

  const copyLinkJornada = (jornadaId) => {
    const link = `${window.location.origin}/capacitaciones/${id}/asistencia/${jornadaId}`;
    navigator.clipboard.writeText(link);
    setLinkCopiedJornada(jornadaId);
    setTimeout(() => setLinkCopiedJornada(null), 2000);
  };

  const handleToggleAsistencia = async (inscriptoId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/capacitaciones/admin/inscriptos/${inscriptoId}/toggle-asistencia`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      alert('Error al actualizar asistencia');
    }
  };

  const handleEmitirDocumento = async (inscriptoId, tipo) => {
    setEmitiendo(inscriptoId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/capacitaciones/admin/inscriptos/${inscriptoId}/emitir/${tipo}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${tipo}_${inscriptoId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Parsear error desde blob (responseType: 'blob' enmascara el detail)
      let errorMsg = 'Error al emitir documento';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          errorMsg = parsed.detail || errorMsg;
        } catch {
          errorMsg = `Error ${err.response?.status || ''} al emitir documento`;
        }
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      alert(errorMsg);
    } finally {
      setEmitiendo(null);
    }
  };

  const handleEmisionMasiva = async (tipo) => {
    const conAsistencia = inscriptos.filter(i => i.asistio);
    if (conAsistencia.length === 0) {
      alert('No hay inscriptos con asistencia para emitir documentos');
      return;
    }
    
    if (!confirm(`¿Generar ${tipo}s para ${conAsistencia.length} personas? Se descargará un único archivo ZIP.`)) return;
    
    setEmitiendo('masivo');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/capacitaciones/admin/cursos/${id}/emitir-masivo/${tipo}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${tipo}s_${conAsistencia.length}_personas.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Cuando responseType es blob, hay que parsear el error desde el blob
      let errorMsg = 'Error al generar emisión masiva';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          errorMsg = parsed.detail || errorMsg;
        } catch {
          errorMsg = `Error ${err.response?.status || ''}: revisá el log del servidor`;
        }
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      alert(errorMsg);
    } finally {
      setEmitiendo(null);
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/capacitaciones/admin/cursos/${id}/exportar-csv`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inscriptos_${curso?.nombre || id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Error al exportar CSV');
    }
  };

  // Reenviar email de confirmación
  const handleReenviarConfirmacion = async (inscriptoId, email) => {
    if (!confirm(`¿Reenviar email de confirmación a ${email}?`)) return;
    
    setReenviando(inscriptoId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/capacitaciones/admin/inscripciones/${inscriptoId}/reenviar-confirmacion`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Email reenviado a ${email}`);
    } catch (err) {
      alert('Error al reenviar email: ' + (err.response?.data?.detail || err.message));
    } finally {
      setReenviando(null);
    }
  };

  // Enviar link de clase virtual a todos
  const handleEnviarLinkVirtual = async (e) => {
    e.preventDefault();
    if (!linkVirtualForm.link) {
      alert('Ingresá el link de la clase');
      return;
    }
    
    const inscriptosActivos = inscriptos.filter(i => !i.cancelada);
    if (!confirm(`¿Enviar link de clase a ${inscriptosActivos.length} inscriptos?`)) return;
    
    setEnviandoLinkVirtual(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/capacitaciones/admin/cursos/${id}/enviar-link-virtual`,
        linkVirtualForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Si el proceso está en background, mostrar mensaje y verificar estado periódicamente
      if (response.data.en_proceso) {
        alert(`${response.data.message}\n\nPodés cerrar esta ventana. El envío continúa en segundo plano.`);
        
        // Polling para verificar estado (opcional)
        const envioId = response.data.envio_id;
        let intentos = 0;
        const maxIntentos = 60; // 5 minutos máximo (cada 5 segundos)
        
        const checkEstado = setInterval(async () => {
          intentos++;
          try {
            const estadoRes = await axios.get(
              `${API_URL}/capacitaciones/admin/envio-link-virtual/${envioId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (estadoRes.data.estado !== 'en_proceso') {
              clearInterval(checkEstado);
              const { enviados, errores, estado } = estadoRes.data;
              if (estado === 'completado') {
                alert(`✅ Envío completado: ${enviados} emails enviados, ${errores} errores`);
              } else if (estado === 'limite_alcanzado') {
                alert(`⚠️ Límite de Gmail alcanzado: ${enviados} emails enviados. Los restantes se pueden reenviar más tarde.`);
              } else {
                alert(`❌ Error en el envío: ${estadoRes.data.error || 'Error desconocido'}`);
              }
            }
          } catch (err) {
            // Silenciar errores de polling
          }
          
          if (intentos >= maxIntentos) {
            clearInterval(checkEstado);
          }
        }, 5000);
        
      } else {
        // Respuesta inmediata (todos ya recibieron el link)
        alert(response.data.message);
      }
      
      setShowLinkVirtualModal(false);
      setLinkVirtualForm({ link: '', fecha: '', mensaje_adicional: '', force_resend: false });
    } catch (err) {
      alert('Error al enviar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setEnviandoLinkVirtual(false);
    }
  };

  // Enviar certificados por email a quienes acreditan (asistencia + criterio min_jornadas)
  const handleEnviarCertificadosEmail = async (e) => {
    e.preventDefault();
    
    // Usa el flag `acredita` que viene del backend (considera min_jornadas_acreditacion).
    // Fallback: legacy `asistio` para cursos previos sin el flag.
    const acreditan = inscriptos.filter(i => !i.cancelada && (i.acredita === true || (i.acredita === undefined && i.asistio)));
    if (acreditan.length === 0) {
      alert('No hay inscriptos que acrediten el curso según el criterio configurado.');
      return;
    }
    
    const tipoLabel = certificadosForm.tipo === 'certificado' ? 'certificados' : 'constancias';
    if (!confirm(`¿Enviar ${tipoLabel} por email a ${acreditan.length} acreditadxs?`)) return;
    
    setEnviandoCertificados(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/capacitaciones/admin/cursos/${id}/enviar-certificados-email`,
        certificadosForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.en_proceso) {
        alert(`${response.data.message}\n\nPodés cerrar esta ventana. El envío continúa en segundo plano.`);
        
        // Polling para verificar estado
        const envioId = response.data.envio_id;
        let intentos = 0;
        const maxIntentos = 120; // 10 minutos máximo (cada 5 segundos)
        
        const checkEstado = setInterval(async () => {
          intentos++;
          try {
            const estadoRes = await axios.get(
              `${API_URL}/capacitaciones/admin/envio-certificados/${envioId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (estadoRes.data.estado !== 'en_proceso') {
              clearInterval(checkEstado);
              const { enviados, errores, estado } = estadoRes.data;
              if (estado === 'completado') {
                alert(`✅ Envío completado: ${enviados} ${tipoLabel} enviados, ${errores} errores`);
              } else if (estado === 'limite_alcanzado') {
                alert(`⚠️ Límite de Gmail alcanzado: ${enviados} ${tipoLabel} enviados. Los restantes se pueden reenviar más tarde.`);
              } else {
                alert(`❌ Error en el envío: ${estadoRes.data.error || 'Error desconocido'}`);
              }
              fetchData(); // Recargar para ver marcas de enviado
            }
          } catch (err) {
            // Silenciar errores de polling
          }
          
          if (intentos >= maxIntentos) {
            clearInterval(checkEstado);
          }
        }, 5000);
        
      } else {
        alert(response.data.message);
      }
      
      setShowEnviarCertificadosModal(false);
      setCertificadosForm({ tipo: 'certificado', mensaje_adicional: '', force_resend: false });
    } catch (err) {
      alert('Error al enviar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setEnviandoCertificados(false);
    }
  };

  // Abrir modal para enviar certificado individual
  const handleAbrirEnviarIndividual = (inscripto) => {
    setInscriptoSeleccionado(inscripto);
    setCertificadoIndividualForm({ tipo: 'constancia', mensaje_adicional: '' });
    setShowEnviarIndividualModal(true);
  };

  // Enviar certificado individual
  const handleEnviarCertificadoIndividual = async (e) => {
    e.preventDefault();
    if (!inscriptoSeleccionado) return;
    
    setEnviandoIndividual(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/capacitaciones/admin/cursos/${id}/enviar-certificado-individual`,
        {
          inscripto_id: inscriptoSeleccionado.id,
          tipo: certificadoIndividualForm.tipo,
          mensaje_adicional: certificadoIndividualForm.mensaje_adicional
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`✅ ${certificadoIndividualForm.tipo === 'certificado' ? 'Certificado' : 'Constancia'} enviado a ${inscriptoSeleccionado.email}`);
      setShowEnviarIndividualModal(false);
      fetchData(); // Recargar para ver el estado actualizado
    } catch (err) {
      alert('Error al enviar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setEnviandoIndividual(false);
    }
  };

  // Cargar historial de envíos de certificados
  const handleVerHistorialEnvios = async () => {
    setCargandoHistorial(true);
    setShowHistorialEnviosModal(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/capacitaciones/admin/cursos/${id}/historial-envios-certificados`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistorialEnvios(response.data);
    } catch (err) {
      alert('Error al cargar historial: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCargandoHistorial(false);
    }
  };

  const handleAddInscripto = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/capacitaciones/cursos/${id}/inscribirse-admin`, formInscripto, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAddModal(false);
      setFormInscripto({ nombre: '', apellido: '', dni: '', telefono: '', formacion_academica: '', matricula: '', distrito: '', email: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al agregar inscripto');
    }
  };

  // Editar inscripto
  const handleEditClick = (inscripto) => {
    setEditingInscripto(inscripto);
    setFormInscripto({
      nombre: inscripto.nombre || '',
      apellido: inscripto.apellido || '',
      dni: inscripto.dni || '',
      telefono: inscripto.telefono || '',
      formacion_academica: inscripto.formacion_academica || '',
      matricula: inscripto.matricula || '',
      distrito: inscripto.distrito || '',
      email: inscripto.email || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateInscripto = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/capacitaciones/admin/inscriptos/${editingInscripto.id}`, formInscripto, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEditModal(false);
      setEditingInscripto(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al actualizar inscripto');
    }
  };

  const handleDeleteInscripto = async (inscriptoId, nombre) => {
    if (!confirm(`¿Eliminar a ${nombre}? Esta acción no se puede deshacer.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/capacitaciones/admin/inscriptos/${inscriptoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      alert('Error al eliminar inscripto');
    }
  };

  // Toggle link de asistencia
  const handleToggleAsistenciaLink = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API_URL}/capacitaciones/admin/cursos/${id}/toggle-asistencia-link`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurso(prev => ({ ...prev, asistencia_link_activo: response.data.activo }));
    } catch (err) {
      alert('Error al actualizar link de asistencia');
    }
  };

  const copyAsistenciaLink = () => {
    const link = `${window.location.origin}/capacitaciones/${id}/asistencia`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Emitir certificado disertante
  const handleEmitirCertificadoDisertante = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/capacitaciones/admin/cursos/${id}/emitir-certificado-disertante`,
        disertanteForm,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificado_disertante_${disertanteForm.dni}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowDisertanteModal(false);
      setDisertanteForm({ nombre: '', dni: '', rol: 'Disertante' });
    } catch (err) {
      alert('Error al emitir certificado');
    }
  };

  const handleUpdateNota = async (inscriptoId) => {
    try {
      const token = localStorage.getItem('token');
      let payload;
      if (notaTipo === 'aprobado' || notaTipo === 'desaprobado') {
        payload = { estado_aprobacion: notaTipo, nota: null };
      } else if (notaTipo === 'ninguno' || notaValue === '' || notaValue === null) {
        payload = { nota: null, estado_aprobacion: null };
      } else {
        const parsed = parseFloat(notaValue);
        if (isNaN(parsed)) {
          alert('Ingresá una nota numérica válida (0-10).');
          return;
        }
        payload = { nota: parsed, estado_aprobacion: null };
      }
      await axios.patch(`${API_URL}/capacitaciones/admin/inscriptos/${inscriptoId}/nota`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingNota(null);
      fetchData();
    } catch (err) {
      alert('Error al actualizar nota');
    }
  };

  // Función de ordenamiento
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Renderizar icono de ordenamiento
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUp className="w-4 h-4 opacity-30" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4" /> 
      : <ChevronDown className="w-4 h-4" />;
  };

  // Filtrado y ordenamiento combinados
  const filteredAndSortedInscriptos = useMemo(() => {
    let result = inscriptos;
    
    // 1. Filtrar por confirmación (excluyendo cancelados por defecto)
    if (filtroConfirmacion === 'todos') {
      result = result.filter(i => !i.cancelada);
    } else if (filtroConfirmacion === 'confirmados') {
      result = result.filter(i => !i.cancelada && i.asistencia_confirmada);
    } else if (filtroConfirmacion === 'no_confirmo') {
      result = result.filter(i => !i.cancelada && !i.asistencia_confirmada);
    } else if (filtroConfirmacion === 'cancelados') {
      result = result.filter(i => i.cancelada);
    }
    
    // 2. Filtrar por matriculado de Lomas (verificado contra BD de matriculades)
    if (filtroMatriculado === 'lomas') {
      result = result.filter(i => i.es_matriculado_lz);
    } else if (filtroMatriculado === 'otros') {
      result = result.filter(i => !i.es_matriculado_lz);
    } else if (filtroMatriculado === 'error_matricula') {
      result = result.filter(i => i.es_matriculado_lz && i.matriculado_lz_via === 'dni');
    }
    
    // 3. Filtrar por búsqueda
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(i =>
        i.nombre?.toLowerCase().includes(search) ||
        i.apellido?.toLowerCase().includes(search) ||
        i.dni?.includes(searchTerm) ||
        i.email?.toLowerCase().includes(search)
      );
    }
    
    // 4. Ordenar
    result = [...result].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Manejar campos especiales
      if (sortConfig.key === 'nombre_completo') {
        aVal = `${a.apellido || ''} ${a.nombre || ''}`.toLowerCase();
        bVal = `${b.apellido || ''} ${b.nombre || ''}`.toLowerCase();
      } else if (sortConfig.key === 'asistio') {
        // Porcentaje de jornadas asistidas (cursos de una jornada: 0 o 100)
        const pct = (x) => (jornadas.length > 1 ? (x.asistencias_resumen?.porcentaje || 0) : (x.asistio ? 100 : 0));
        aVal = pct(a);
        bVal = pct(b);
      } else if (sortConfig.key === 'asistencia_confirmada') {
        // Convertir booleanos a números para ordenar (true = 1, false = 0)
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      } else if (sortConfig.key === 'acredita') {
        // acredita puede ser undefined → calcular como en la columna
        const av = a.acredita === true || (a.acredita === undefined && a.asistio);
        const bv = b.acredita === true || (b.acredita === undefined && b.asistio);
        aVal = av ? 1 : 0;
        bVal = bv ? 1 : 0;
      } else if (sortConfig.key === 'es_matriculado_lz') {
        aVal = a.es_matriculado_lz ? 1 : 0;
        bVal = b.es_matriculado_lz ? 1 : 0;
      } else if (typeof aVal === 'string') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
      } else {
        aVal = aVal || '';
        bVal = bVal || '';
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return `${a.apellido || ''} ${a.nombre || ''}`.localeCompare(`${b.apellido || ''} ${b.nombre || ''}`, 'es');
    });
    
    return result;
  }, [inscriptos, filtroConfirmacion, filtroMatriculado, searchTerm, sortConfig, jornadas]);

  // Estadísticas de confirmación
  const statsConfirmacion = useMemo(() => {
    const activos = inscriptos.filter(i => !i.cancelada);
    return {
      total: activos.length,
      confirmados: activos.filter(i => i.asistencia_confirmada).length,
      noConfirmo: activos.filter(i => !i.asistencia_confirmada).length,
      cancelados: inscriptos.filter(i => i.cancelada).length
    };
  }, [inscriptos]);

  // Estadísticas de matriculados (basadas en verificación contra BD)
  const statsMatriculado = useMemo(() => {
    const activos = inscriptos.filter(i => !i.cancelada);
    return {
      total: activos.length,
      lomas: activos.filter(i => i.es_matriculado_lz).length,
      otros: activos.filter(i => !i.es_matriculado_lz).length,
      errorMatricula: activos.filter(i => i.es_matriculado_lz && i.matriculado_lz_via === 'dni').length,
    };
  }, [inscriptos]);

  // Mantener compatibilidad con código existente
  const filteredInscriptos = filteredAndSortedInscriptos;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const tieneExamen = curso?.tiene_examen;
  const asistenciaLinkActivo = curso?.asistencia_link_activo;
  const asistenciaLink = `${window.location.origin}/capacitaciones/${id}/asistencia`;

  return (
    <TooltipProvider>
      <div>
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/capacitaciones/cursos')}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Volver a cursos</TooltipContent>
          </Tooltip>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">Inscriptos</h1>
            <p className="text-gray-600">{curso?.nombre}</p>
          </div>
        </div>

        {/* Link de Asistencia */}
        <Card className="mb-6 border-emerald-200 bg-emerald-50/50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-start gap-3">
                <Link2 className="w-5 h-5 text-emerald-600 mt-1" />
                <div>
                  <p className="font-medium text-emerald-800">
                    {jornadas.length > 1 ? 'Links de Asistencia por Jornada' : 'Link de Asistencia'}
                  </p>
                  <p className="text-sm text-emerald-600">
                    {jornadas.length > 1
                      ? 'Cada jornada tiene su propio link. Activá y copiá el de cada clase.'
                      : 'Compartí este link durante la clase para que los asistentes registren su presente'}
                  </p>
                </div>
              </div>
              {jornadas.length <= 1 && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{asistenciaLinkActivo ? 'Activo' : 'Inactivo'}</span>
                    <Switch
                      checked={asistenciaLinkActivo}
                      onCheckedChange={handleToggleAsistenciaLink}
                    />
                  </div>
                  {asistenciaLinkActivo && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyAsistenciaLink}
                      className="gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      {linkCopied ? '¡Copiado!' : 'Copiar link'}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Modo single jornada: muestra el link plano */}
            {jornadas.length <= 1 && asistenciaLinkActivo && (
              <div className="mt-3 p-2 bg-white rounded border text-sm text-gray-600 break-all">
                {asistenciaLink}
              </div>
            )}

            {/* Modo multi-jornada: una fila por cada jornada */}
            {jornadas.length > 1 && (
              <div className="mt-3 space-y-2">
                {jornadas.map((j, idx) => {
                  const linkJ = `${window.location.origin}/capacitaciones/${id}/asistencia/${j.id}`;
                  return (
                    <div key={j.id} className="flex items-center gap-3 bg-white border rounded-md p-2.5">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800">
                          {j.etiqueta} <span className="text-gray-500 font-normal">· {j.fecha}</span>
                        </div>
                        {j.asistencia_link_activo && (
                          <div className="text-xs text-gray-500 truncate" data-testid={`link-jornada-${idx}`}>
                            {linkJ}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{j.asistencia_link_activo ? 'Activo' : 'Inactivo'}</span>
                        <Switch
                          checked={!!j.asistencia_link_activo}
                          onCheckedChange={() => handleToggleLinkJornada(j.id)}
                          data-testid={`toggle-link-jornada-${idx}`}
                        />
                        {j.asistencia_link_activo && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyLinkJornada(j.id)}
                            className="gap-1.5"
                            data-testid={`copy-link-jornada-${idx}`}
                          >
                            <Copy className="w-3.5 h-3.5" />
                            {linkCopiedJornada === j.id ? '¡Copiado!' : 'Copiar'}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-2xl font-bold">{inscriptos.length}</p>
              <p className="text-sm text-gray-500">Total inscriptos</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-2xl font-bold text-emerald-600">{inscriptos.filter(i => i.asistio).length}</p>
              <p className="text-sm text-gray-500">Asistentes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-2xl font-bold text-amber-600">{inscriptos.filter(i => !i.asistio).length}</p>
              <p className="text-sm text-gray-500">Sin asistencia</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-2xl font-bold">{curso?.duracion_horas || '-'}h</p>
              <p className="text-sm text-gray-500">Duración</p>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button onClick={() => { setFormInscripto({ nombre: '', apellido: '', dni: '', telefono: '', formacion_academica: '', matricula: '', distrito: '', email: '' }); setShowAddModal(true); }} data-testid="add-inscripto-btn">
                <Plus className="w-4 h-4 mr-2" />
                Agregar inscripto
              </Button>
            </TooltipTrigger>
            <TooltipContent>Agregar un inscripto manualmente</TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" onClick={() => navigate(`/admin/capacitaciones/cursos/${id}/importar`)}>
                <Upload className="w-4 h-4 mr-2" />
                Importar CSV
              </Button>
            </TooltipTrigger>
            <TooltipContent>Importar inscriptos desde archivo CSV</TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" onClick={handleExportCSV}>
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
            </TooltipTrigger>
            <TooltipContent>Descargar listado de inscriptos</TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" onClick={() => handleEmisionMasiva('constancia')}>
                <FileText className="w-4 h-4 mr-2" />
                Emitir Constancias
              </Button>
            </TooltipTrigger>
            <TooltipContent>Emitir constancias para todos los asistentes</TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" onClick={() => handleEmisionMasiva('certificado')}>
                <Award className="w-4 h-4 mr-2" />
                Emitir Certificados
              </Button>
            </TooltipTrigger>
            <TooltipContent>Emitir certificados para todos los asistentes</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50" onClick={() => setShowDisertanteModal(true)}>
                <Mic className="w-4 h-4 mr-2" />
                Certificado Disertante
              </Button>
            </TooltipTrigger>
            <TooltipContent>Emitir certificado para disertantes/expositores</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={() => setShowLinkVirtualModal(true)}
              >
                <Video className="w-4 h-4 mr-2" />
                Enviar Link
              </Button>
            </TooltipTrigger>
            <TooltipContent>Enviar link de clase (Zoom, Meet, etc.) a todos los inscriptos</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={() => setShowEnviarCertificadosModal(true)}
                disabled={inscriptos.filter(i => i.asistio && !i.cancelada).length === 0}
              >
                <Mail className="w-4 h-4 mr-2" />
                Enviar Certificados
              </Button>
            </TooltipTrigger>
            <TooltipContent>Enviar certificados/constancias por email a quienes asistieron</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-gray-500 hover:text-gray-700"
                onClick={handleVerHistorialEnvios}
              >
                <History className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Ver historial de envíos de certificados</TooltipContent>
          </Tooltip>
        </div>

        {/* Filtros y Estadísticas de Confirmación */}
        <Card className="mb-4">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-600">Filtrar por confirmación:</span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={filtroConfirmacion === 'todos' ? 'default' : 'outline'}
                  onClick={() => setFiltroConfirmacion('todos')}
                  className="h-8"
                >
                  Todos activos
                  <Badge variant="secondary" className="ml-2 bg-gray-200">{statsConfirmacion.total}</Badge>
                </Button>
                
                <Button
                  size="sm"
                  variant={filtroConfirmacion === 'confirmados' ? 'default' : 'outline'}
                  onClick={() => setFiltroConfirmacion('confirmados')}
                  className={`h-8 ${filtroConfirmacion === 'confirmados' ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-emerald-700 border-emerald-300 hover:bg-emerald-50'}`}
                >
                  <Check className="w-3 h-3 mr-1" />
                  Confirmados
                  <Badge variant="secondary" className="ml-2 bg-emerald-100 text-emerald-700">{statsConfirmacion.confirmados}</Badge>
                </Button>
                
                <Button
                  size="sm"
                  variant={filtroConfirmacion === 'no_confirmo' ? 'default' : 'outline'}
                  onClick={() => setFiltroConfirmacion('no_confirmo')}
                  className={`h-8 ${filtroConfirmacion === 'no_confirmo' ? 'bg-amber-600 hover:bg-amber-700' : 'text-amber-700 border-amber-300 hover:bg-amber-50'}`}
                >
                  <X className="w-3 h-3 mr-1" />
                  No confirmó
                  <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-700">{statsConfirmacion.noConfirmo}</Badge>
                </Button>
                
                <Button
                  size="sm"
                  variant={filtroConfirmacion === 'cancelados' ? 'default' : 'outline'}
                  onClick={() => setFiltroConfirmacion('cancelados')}
                  className={`h-8 ${filtroConfirmacion === 'cancelados' ? 'bg-red-600 hover:bg-red-700' : 'text-red-700 border-red-300 hover:bg-red-50'}`}
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Cancelados
                  <Badge variant="secondary" className="ml-2 bg-red-100 text-red-700">{statsConfirmacion.cancelados}</Badge>
                </Button>
              </div>
            </div>
            
            {/* Filtro por Matriculado de Lomas */}
            <div className="flex flex-wrap items-center gap-4 pt-3 border-t">
              <div className="flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-600">Matriculades de Lomas:</span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={filtroMatriculado === 'todos' ? 'default' : 'outline'}
                  onClick={() => setFiltroMatriculado('todos')}
                  className="h-8"
                  data-testid="filter-lz-todos"
                >
                  Todos
                  <Badge variant="secondary" className="ml-2 bg-gray-200">{statsMatriculado.total}</Badge>
                </Button>
                
                <Button
                  size="sm"
                  variant={filtroMatriculado === 'lomas' ? 'default' : 'outline'}
                  onClick={() => setFiltroMatriculado('lomas')}
                  className={`h-8 ${filtroMatriculado === 'lomas' ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-emerald-700 border-emerald-300 hover:bg-emerald-50'}`}
                  data-testid="filter-lz-lomas"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  De Lomas
                  <Badge variant="secondary" className="ml-2 bg-emerald-100 text-emerald-700">{statsMatriculado.lomas}</Badge>
                </Button>
                
                <Button
                  size="sm"
                  variant={filtroMatriculado === 'otros' ? 'default' : 'outline'}
                  onClick={() => setFiltroMatriculado('otros')}
                  className={`h-8 ${filtroMatriculado === 'otros' ? 'bg-gray-600 hover:bg-gray-700' : 'text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  data-testid="filter-lz-otros"
                >
                  Externos
                  <Badge variant="secondary" className="ml-2 bg-gray-100 text-gray-700">{statsMatriculado.otros}</Badge>
                </Button>

                {statsMatriculado.errorMatricula > 0 && (
                  <Button
                    size="sm"
                    variant={filtroMatriculado === 'error_matricula' ? 'default' : 'outline'}
                    onClick={() => setFiltroMatriculado('error_matricula')}
                    className={`h-8 ${filtroMatriculado === 'error_matricula' ? 'bg-amber-600 hover:bg-amber-700' : 'text-amber-700 border-amber-300 hover:bg-amber-50'}`}
                    data-testid="filter-lz-error-matricula"
                    title="Inscriptos que coinciden por DNI pero no por matrícula (probable typo)"
                  >
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Con error de matrícula
                    <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-700">{statsMatriculado.errorMatricula}</Badge>
                  </Button>
                )}

                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRevalidarLz}
                  disabled={revalidandoLz}
                  className="h-8 ml-auto"
                  data-testid="revalidar-lz-btn"
                  title="Re-verificar todos los inscriptos contra la BD de matriculades"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${revalidandoLz ? 'animate-spin' : ''}`} />
                  {revalidandoLz ? 'Re-validando…' : 'Re-validar'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Buscar por nombre, DNI o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-center p-3 font-medium text-gray-600 w-12">#</th>
                    <th 
                      className="text-left p-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('nombre_completo')}
                    >
                      <div className="flex items-center gap-1">
                        Nombre
                        <SortIcon columnKey="nombre_completo" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('dni')}
                    >
                      <div className="flex items-center gap-1">
                        DNI
                        <SortIcon columnKey="dni" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('matricula')}
                    >
                      <div className="flex items-center gap-1">
                        Matrícula
                        <SortIcon columnKey="matricula" />
                      </div>
                    </th>
                    <th
                      className="text-center p-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('es_matriculado_lz')}
                      title="¿Es matriculade del Colegio LZ? Verificado contra BD"
                    >
                      <div className="flex items-center justify-center gap-1">
                        LZ
                        <SortIcon columnKey="es_matriculado_lz" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('email')}
                    >
                      <div className="flex items-center gap-1">
                        Email
                        <SortIcon columnKey="email" />
                      </div>
                    </th>
                    <th 
                      className="text-center p-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('asistencia_confirmada')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Confirmó
                        <SortIcon columnKey="asistencia_confirmada" />
                      </div>
                    </th>
                    <th 
                      className="text-center p-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('asistio')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Asistencia {jornadas.length > 1 && <span className="text-xs text-gray-400">({jornadas.length} jornadas)</span>}
                        <SortIcon columnKey="asistio" />
                      </div>
                      {/* Sub-encabezado con etiquetas de jornadas */}
                      {jornadas.length > 0 && (
                        <div className="flex justify-center gap-1.5 mt-1 text-xs font-normal text-gray-500">
                          {jornadas.map((j, idx) => (
                            <span key={j.id} className="inline-block w-7 text-center" title={`${j.etiqueta} - ${j.fecha}`}>
                              {`J${idx + 1}`}
                            </span>
                          ))}
                          {jornadas.length > 1 && <span className="inline-block w-10 text-center">%</span>}
                        </div>
                      )}
                    </th>
                    {tieneExamen && <th className="text-center p-3 font-medium text-gray-600">Nota</th>}
                    <th
                      className="text-center p-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('acredita')}
                      title={curso?.min_jornadas_acreditacion
                        ? `Acredita si asistió a al menos ${curso.min_jornadas_acreditacion} de ${jornadas.length} jornadas`
                        : 'Acredita según la asistencia configurada del curso'}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Acredita
                        <SortIcon columnKey="acredita" />
                      </div>
                    </th>
                    <th className="text-center p-3 font-medium text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInscriptos.length === 0 ? (
                    <tr>
                      <td colSpan={tieneExamen ? 11 : 10} className="text-center py-8 text-gray-500">
                        No hay inscriptos {filtroConfirmacion !== 'todos' && `con el filtro seleccionado`}
                      </td>
                    </tr>
                  ) : (
                    filteredInscriptos.map((inscripto, index) => (
                      <tr key={inscripto.id} className={`border-b hover:bg-gray-50 ${inscripto.cancelada ? 'bg-red-50 opacity-60' : ''}`}>
                        <td className="p-3 text-center text-gray-500 font-medium">{index + 1}</td>
                        <td className="p-3">
                          <p className="font-medium">{inscripto.apellido}, {inscripto.nombre}</p>
                          <p className="text-xs text-gray-500">{inscripto.formacion_academica}</p>
                          {inscripto.cancelada && (
                            <Badge variant="destructive" className="mt-1 text-xs">Cancelado</Badge>
                          )}
                        </td>
                        <td className="p-3 text-gray-600">{inscripto.dni}</td>
                        <td className="p-3 text-gray-600">{inscripto.matricula || '-'}</td>
                        <td className="p-3 text-center">
                          {inscripto.es_matriculado_lz ? (
                            inscripto.matriculado_lz_via === 'matricula' ? (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" title="Coincide por número de matrícula">
                                <Check className="w-3 h-3 mr-1" />
                                LZ
                              </Badge>
                            ) : (
                              <Badge
                                className="bg-amber-100 text-amber-700 hover:bg-amber-100"
                                title="Coincide por DNI pero no por matrícula. Posible typo del inscripto."
                              >
                                <AlertCircle className="w-3 h-3 mr-1" />
                                LZ (DNI)
                              </Badge>
                            )
                          ) : (
                            <Badge variant="outline" className="text-gray-500 border-gray-300" title="No figura en la BD de matriculades">
                              <X className="w-3 h-3 mr-1" />
                              No
                            </Badge>
                          )}
                        </td>
                        <td className="p-3 text-gray-600 text-sm">{inscripto.email}</td>
                        <td className="p-3 text-center">
                          {inscripto.cancelada ? (
                            <span className="text-red-500 text-sm">-</span>
                          ) : inscripto.asistencia_confirmada ? (
                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                              <Check className="w-3 h-3 mr-1" />
                              Sí
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              <X className="w-3 h-3 mr-1" />
                              No
                            </Badge>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {inscripto.cancelada ? (
                            <span className="text-red-500 text-sm">-</span>
                          ) : jornadas.length === 0 ? (
                            <Button
                              variant={inscripto.asistio ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleToggleAsistencia(inscripto.id)}
                              className={inscripto.asistio ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                            >
                              {inscripto.asistio ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                            </Button>
                          ) : (
                            <div className="flex items-center justify-center gap-1.5">
                              {jornadas.map((j) => {
                                const presente = !!inscripto.asistencias_resumen?.asistencias?.[j.id];
                                const key = `${inscripto.id}_${j.id}`;
                                const cargando = togglingJornada === key;
                                return (
                                  <button
                                    key={j.id}
                                    onClick={() => handleToggleAsistenciaJornada(inscripto.id, j.id)}
                                    disabled={cargando}
                                    title={`${j.etiqueta} (${j.fecha})`}
                                    className={`w-7 h-7 rounded-md text-xs font-bold transition-colors ${
                                      presente
                                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200 border border-gray-300'
                                    }`}
                                    data-testid={`asistencia-${inscripto.id}-${j.id}`}
                                  >
                                    {presente ? '✓' : '–'}
                                  </button>
                                );
                              })}
                              {jornadas.length > 1 && (
                                <span
                                  className="inline-block w-10 text-center text-xs font-semibold text-gray-700"
                                  title={`${inscripto.asistencias_resumen?.jornadas_asistidas || 0} de ${jornadas.length}`}
                                >
                                  {Math.round(inscripto.asistencias_resumen?.porcentaje || 0)}%
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        {tieneExamen && (
                          <td className="p-3 text-center">
                            {editingNota === inscripto.id ? (
                              <div className="flex flex-col items-center gap-1.5 min-w-[170px]" data-testid={`nota-editor-${inscripto.id}`}>
                                <select
                                  value={notaTipo}
                                  onChange={(e) => setNotaTipo(e.target.value)}
                                  className="h-8 border border-gray-300 rounded text-xs px-2 w-full"
                                  data-testid={`nota-tipo-${inscripto.id}`}
                                >
                                  <option value="numero">Nota numérica</option>
                                  <option value="aprobado">Aprobado (sin nota)</option>
                                  <option value="desaprobado">Desaprobado</option>
                                  <option value="ninguno">Sin calificar</option>
                                </select>
                                <div className="flex items-center gap-1">
                                  {notaTipo === 'numero' && (
                                    <Input
                                      type="number"
                                      min="0"
                                      max="10"
                                      step="0.1"
                                      value={notaValue}
                                      onChange={(e) => setNotaValue(e.target.value)}
                                      className="w-20 h-8 text-center"
                                      autoFocus
                                      data-testid={`nota-input-${inscripto.id}`}
                                    />
                                  )}
                                  <Button size="sm" variant="ghost"
                                    onClick={() => handleUpdateNota(inscripto.id)}
                                    data-testid={`nota-save-${inscripto.id}`}>
                                    <Check className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost"
                                    onClick={() => setEditingNota(null)}>
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingNota(inscripto.id);
                                  if (inscripto.estado_aprobacion === 'aprobado') {
                                    setNotaTipo('aprobado');
                                    setNotaValue('');
                                  } else if (inscripto.estado_aprobacion === 'desaprobado') {
                                    setNotaTipo('desaprobado');
                                    setNotaValue('');
                                  } else if (inscripto.nota !== undefined && inscripto.nota !== null) {
                                    setNotaTipo('numero');
                                    setNotaValue(inscripto.nota?.toString() || '');
                                  } else {
                                    setNotaTipo('numero');
                                    setNotaValue('');
                                  }
                                }}
                                data-testid={`nota-cell-${inscripto.id}`}
                              >
                                {inscripto.estado_aprobacion === 'aprobado' ? (
                                  <span className="text-green-700 font-medium text-xs">Aprobado</span>
                                ) : inscripto.estado_aprobacion === 'desaprobado' ? (
                                  <span className="text-red-600 font-medium text-xs">Desaprobado</span>
                                ) : inscripto.nota !== undefined && inscripto.nota !== null ? (
                                  inscripto.nota
                                ) : (
                                  '-'
                                )}
                              </Button>
                            )}
                          </td>
                        )}
                        <td className="p-3 text-center">
                          {(() => {
                            const acredita = inscripto.acredita === true || (inscripto.acredita === undefined && inscripto.asistio);
                            const asistidas = inscripto.asistencias_resumen?.jornadas_asistidas;
                            const minJ = curso?.min_jornadas_acreditacion;
                            const totalJ = jornadas.length;
                            const tooltip = totalJ > 0
                              ? `${asistidas || 0}/${totalJ} jornadas${minJ ? ` (mín. ${minJ})` : ''}`
                              : (acredita ? 'Cumple criterio' : 'No cumple criterio');
                            return inscripto.cancelada ? (
                              <span className="text-gray-300 text-xs">—</span>
                            ) : acredita ? (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" title={tooltip}>
                                <Check className="w-3 h-3 mr-1" />
                                Sí
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-rose-600 border-rose-300" title={tooltip}>
                                <X className="w-3 h-3 mr-1" />
                                No
                              </Badge>
                            );
                          })()}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" onClick={() => handleEditClick(inscripto)}>
                                  <Edit2 className="w-4 h-4 text-blue-600" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Editar datos</TooltipContent>
                            </Tooltip>
                            
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReenviarConfirmacion(inscripto.id, inscripto.email)}
                                  disabled={reenviando === inscripto.id || inscripto.cancelada}
                                >
                                  {reenviando === inscripto.id ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Mail className="w-4 h-4 text-green-600" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Reenviar confirmación</TooltipContent>
                            </Tooltip>
                            
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEmitirDocumento(inscripto.id, 'constancia')}
                                  disabled={emitiendo === inscripto.id || !inscripto.asistio}
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Emitir constancia</TooltipContent>
                            </Tooltip>
                            
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEmitirDocumento(inscripto.id, 'certificado')}
                                  disabled={emitiendo === inscripto.id || !inscripto.asistio}
                                >
                                  <Award className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Emitir certificado</TooltipContent>
                            </Tooltip>

                            {/* Botón para enviar certificado por email individual */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleAbrirEnviarIndividual(inscripto)}
                                  disabled={!inscripto.asistio || !inscripto.email}
                                  className="text-amber-600 hover:text-amber-700"
                                >
                                  <Send className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Enviar constancia por email</TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleDeleteInscripto(inscripto.id, `${inscripto.nombre} ${inscripto.apellido}`)}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Eliminar</TooltipContent>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Modal Agregar Inscripto */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Agregar Inscripto</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddInscripto} className="space-y-4">
              <div className="bg-violet-50 border border-violet-200 rounded-md p-3 space-y-2">
                <p className="text-xs text-violet-700">
                  Buscá un matriculado con cuenta y autocompletá los datos.
                </p>
                <BuscadorMatriculados
                  onSelect={(m) => setFormInscripto((p) => ({
                    ...p,
                    nombre: m.nombre || p.nombre,
                    apellido: m.apellido || p.apellido,
                    dni: m.dni || p.dni,
                    telefono: m.telefono || p.telefono,
                    email: m.email || p.email,
                    matricula: m.matricula || p.matricula,
                    distrito: m.distrito || p.distrito,
                  }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input id="nombre" value={formInscripto.nombre} onChange={(e) => setFormInscripto(p => ({ ...p, nombre: e.target.value }))} required />
                </div>
                <div>
                  <Label htmlFor="apellido">Apellido *</Label>
                  <Input id="apellido" value={formInscripto.apellido} onChange={(e) => setFormInscripto(p => ({ ...p, apellido: e.target.value }))} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dni">DNI *</Label>
                  <Input id="dni" value={formInscripto.dni} onChange={(e) => setFormInscripto(p => ({ ...p, dni: e.target.value }))} required />
                </div>
                <div>
                  <Label htmlFor="telefono">Teléfono *</Label>
                  <Input id="telefono" value={formInscripto.telefono} onChange={(e) => setFormInscripto(p => ({ ...p, telefono: e.target.value }))} required />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" value={formInscripto.email} onChange={(e) => setFormInscripto(p => ({ ...p, email: e.target.value }))} required />
              </div>
              <div>
                <Label htmlFor="formacion">Formación Académica *</Label>
                <Select value={formInscripto.formacion_academica} onValueChange={(v) => setFormInscripto(p => ({ ...p, formacion_academica: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {FORMACIONES.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="matricula">Matrícula</Label>
                  <Input id="matricula" value={formInscripto.matricula} onChange={(e) => setFormInscripto(p => ({ ...p, matricula: e.target.value }))} />
                </div>
                <div>
                  <Label htmlFor="distrito">Distrito</Label>
                  <Input id="distrito" value={formInscripto.distrito} onChange={(e) => setFormInscripto(p => ({ ...p, distrito: e.target.value }))} />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>Cancelar</Button>
                <Button type="submit">Agregar</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Editar Inscripto */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Editar Inscripto</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateInscripto} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-nombre">Nombre *</Label>
                  <Input id="edit-nombre" value={formInscripto.nombre} onChange={(e) => setFormInscripto(p => ({ ...p, nombre: e.target.value }))} required />
                </div>
                <div>
                  <Label htmlFor="edit-apellido">Apellido *</Label>
                  <Input id="edit-apellido" value={formInscripto.apellido} onChange={(e) => setFormInscripto(p => ({ ...p, apellido: e.target.value }))} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-dni">DNI *</Label>
                  <Input id="edit-dni" value={formInscripto.dni} onChange={(e) => setFormInscripto(p => ({ ...p, dni: e.target.value }))} required />
                </div>
                <div>
                  <Label htmlFor="edit-telefono">Teléfono</Label>
                  <Input id="edit-telefono" value={formInscripto.telefono} onChange={(e) => setFormInscripto(p => ({ ...p, telefono: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input id="edit-email" type="email" value={formInscripto.email} onChange={(e) => setFormInscripto(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="edit-formacion">Formación Académica</Label>
                <Select value={formInscripto.formacion_academica} onValueChange={(v) => setFormInscripto(p => ({ ...p, formacion_academica: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {FORMACIONES.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-matricula">Matrícula</Label>
                  <Input id="edit-matricula" value={formInscripto.matricula} onChange={(e) => setFormInscripto(p => ({ ...p, matricula: e.target.value }))} />
                </div>
                <div>
                  <Label htmlFor="edit-distrito">Distrito</Label>
                  <Input id="edit-distrito" value={formInscripto.distrito} onChange={(e) => setFormInscripto(p => ({ ...p, distrito: e.target.value }))} />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>Cancelar</Button>
                <Button type="submit">Guardar Cambios</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Certificado Disertante */}
        <Dialog open={showDisertanteModal} onOpenChange={setShowDisertanteModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mic className="w-5 h-5 text-violet-600" />
                Certificado para Disertante
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEmitirCertificadoDisertante} className="space-y-4">
              <div>
                <Label htmlFor="disertante-nombre">Nombre Completo *</Label>
                <Input 
                  id="disertante-nombre" 
                  value={disertanteForm.nombre} 
                  onChange={(e) => setDisertanteForm(p => ({ ...p, nombre: e.target.value }))} 
                  placeholder="Ej: María Laura González"
                  required 
                />
              </div>
              <div>
                <Label htmlFor="disertante-dni">DNI *</Label>
                <Input 
                  id="disertante-dni" 
                  value={disertanteForm.dni} 
                  onChange={(e) => setDisertanteForm(p => ({ ...p, dni: e.target.value }))} 
                  placeholder="Sin puntos"
                  required 
                />
              </div>
              <div>
                <Label htmlFor="disertante-rol">Rol en el evento *</Label>
                <Select value={disertanteForm.rol} onValueChange={(v) => setDisertanteForm(p => ({ ...p, rol: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES_DISERTANTE.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-sm text-gray-500">
                Se emitirá un certificado que acredita la participación como {disertanteForm.rol.toLowerCase()} en "{curso?.nombre}"
              </p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDisertanteModal(false)}>Cancelar</Button>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700">Emitir Certificado</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Enviar Link Virtual */}
        <Dialog open={showLinkVirtualModal} onOpenChange={setShowLinkVirtualModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Video className="w-5 h-5 text-emerald-600" />
                Enviar Link de Clase Virtual
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEnviarLinkVirtual} className="space-y-4">
              <div>
                <Label>Link de la clase (Zoom, Meet, etc.) *</Label>
                <Input
                  type="url"
                  placeholder="https://zoom.us/j/123456789"
                  value={linkVirtualForm.link}
                  onChange={(e) => setLinkVirtualForm({...linkVirtualForm, link: e.target.value})}
                  required
                />
              </div>
              
              {curso?.fechas && curso.fechas.length > 1 && (
                <div>
                  <Label>Fecha de la clase</Label>
                  <Select
                    value={linkVirtualForm.fecha}
                    onValueChange={(value) => setLinkVirtualForm({...linkVirtualForm, fecha: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar fecha" />
                    </SelectTrigger>
                    <SelectContent>
                      {curso.fechas.map((fecha, idx) => (
                        <SelectItem key={idx} value={fecha}>{fecha}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label>Mensaje adicional (opcional)</Label>
                <Textarea
                  placeholder="Ej: Recuerden tener cámara y micrófono listos..."
                  value={linkVirtualForm.mensaje_adicional}
                  onChange={(e) => setLinkVirtualForm({...linkVirtualForm, mensaje_adicional: e.target.value})}
                  rows={3}
                />
              </div>
              
              {/* Switch para forzar reenvío */}
              <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex-1">
                  <Label className="text-amber-800 font-medium cursor-pointer" htmlFor="force-resend-switch">
                    Volver a enviar a todos
                  </Label>
                  <p className="text-xs text-amber-600 mt-1">
                    Ignora el control de envíos previos y reenvía a todos los inscriptos
                  </p>
                </div>
                <Switch
                  id="force-resend-switch"
                  checked={linkVirtualForm.force_resend}
                  onCheckedChange={(checked) => setLinkVirtualForm({...linkVirtualForm, force_resend: checked})}
                />
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
                <p>Se enviará a <strong>{inscriptos.filter(i => !i.cancelada).length}</strong> inscriptos activos</p>
                {linkVirtualForm.force_resend && (
                  <p className="text-amber-600 mt-1 font-medium">⚠️ Se enviará incluso a quienes ya lo recibieron</p>
                )}
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowLinkVirtualModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={enviandoLinkVirtual} className="bg-emerald-600 hover:bg-emerald-700">
                  {enviandoLinkVirtual ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar a todos
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Enviar Certificados por Email */}
        <Dialog open={showEnviarCertificadosModal} onOpenChange={setShowEnviarCertificadosModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-amber-600" />
                Enviar Certificados por Email
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEnviarCertificadosEmail} className="space-y-4">
              <div>
                <Label>Tipo de documento</Label>
                <Select
                  value={certificadosForm.tipo}
                  onValueChange={(value) => setCertificadosForm({...certificadosForm, tipo: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="certificado">Certificado</SelectItem>
                    <SelectItem value="constancia">Constancia de Asistencia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Mensaje adicional (opcional)</Label>
                <Textarea
                  placeholder="Ej: ¡Felicitaciones por completar el curso!..."
                  value={certificadosForm.mensaje_adicional}
                  onChange={(e) => setCertificadosForm({...certificadosForm, mensaje_adicional: e.target.value})}
                  rows={3}
                />
              </div>
              
              {/* Switch para forzar reenvío */}
              <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex-1">
                  <Label className="text-amber-800 font-medium cursor-pointer" htmlFor="force-resend-cert-switch">
                    Volver a enviar a todos
                  </Label>
                  <p className="text-xs text-amber-600 mt-1">
                    Reenvía incluso a quienes ya recibieron el documento
                  </p>
                </div>
                <Switch
                  id="force-resend-cert-switch"
                  checked={certificadosForm.force_resend}
                  onCheckedChange={(checked) => setCertificadosForm({...certificadosForm, force_resend: checked})}
                />
              </div>
              
              <div className="bg-emerald-50 p-3 rounded-lg text-sm text-emerald-700 border border-emerald-200">
                {(() => {
                  const acreditan = inscriptos.filter(i => !i.cancelada && (i.acredita === true || (i.acredita === undefined && i.asistio)));
                  return (
                    <>
                      <p className="font-medium">📧 Se enviará a {acreditan.length} acreditadxs</p>
                      <p className="text-xs mt-1 text-emerald-600">Solo a quienes cumplen el criterio de acreditación (mínimo de jornadas configurado)</p>
                    </>
                  );
                })()}
                {certificadosForm.force_resend && (
                  <p className="text-amber-600 mt-2 font-medium">⚠️ Se enviará incluso a quienes ya lo recibieron</p>
                )}
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEnviarCertificadosModal(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={enviandoCertificados || inscriptos.filter(i => i.asistio && !i.cancelada).length === 0} 
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {enviandoCertificados ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar {certificadosForm.tipo === 'certificado' ? 'Certificados' : 'Constancias'}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Historial de Envíos */}
        <Dialog open={showHistorialEnviosModal} onOpenChange={setShowHistorialEnviosModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-gray-600" />
                Historial de Envíos de Certificados
              </DialogTitle>
            </DialogHeader>
            
            {cargandoHistorial ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : historialEnvios.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No hay envíos registrados para este curso</p>
            ) : (
              <div className="space-y-4">
                {historialEnvios.map((envio, idx) => (
                  <div key={envio.id || idx} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant={envio.estado === 'completado' ? 'default' : envio.estado === 'error' ? 'destructive' : 'secondary'}>
                          {envio.estado === 'completado' ? '✅ Completado' : 
                           envio.estado === 'error' ? '❌ Error' : 
                           envio.estado === 'limite_alcanzado' ? '⚠️ Límite alcanzado' : 
                           '⏳ En proceso'}
                        </Badge>
                        <span className="text-sm text-gray-500 capitalize">{envio.tipo}</span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {envio.created_at ? new Date(envio.created_at).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' }) : '-'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm mb-2">
                      <div>
                        <span className="text-gray-500">Enviados:</span>
                        <span className="ml-2 font-medium text-emerald-600">{envio.enviados || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Errores:</span>
                        <span className="ml-2 font-medium text-red-600">{envio.errores || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Por:</span>
                        <span className="ml-2 text-gray-700">{envio.created_by || '-'}</span>
                      </div>
                    </div>
                    
                    {/* Mostrar detalle de errores si existen */}
                    {envio.errores_detalle && envio.errores_detalle.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex items-center gap-1 text-red-600 text-sm font-medium mb-2">
                          <AlertCircle className="w-4 h-4" />
                          Detalle de errores ({envio.errores_detalle.length})
                        </div>
                        <div className="bg-red-50 rounded p-2 max-h-40 overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-red-800">
                                <th className="pb-1">Nombre</th>
                                <th className="pb-1">Email</th>
                                <th className="pb-1">Error</th>
                              </tr>
                            </thead>
                            <tbody className="text-red-700">
                              {envio.errores_detalle.map((err, i) => (
                                <tr key={i} className="border-t border-red-100">
                                  <td className="py-1">{err.nombre || '-'}</td>
                                  <td className="py-1">{err.email || '-'}</td>
                                  <td className="py-1 text-red-600">{err.error || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHistorialEnviosModal(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Enviar Certificado Individual */}
        <Dialog open={showEnviarIndividualModal} onOpenChange={setShowEnviarIndividualModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-amber-600" />
                Enviar Constancia Individual
              </DialogTitle>
            </DialogHeader>
            {inscriptoSeleccionado && (
              <form onSubmit={handleEnviarCertificadoIndividual} className="space-y-4">
                {/* Info del destinatario */}
                <div className="bg-gray-50 p-3 rounded-lg border">
                  <p className="font-medium text-gray-900">
                    {inscriptoSeleccionado.apellido}, {inscriptoSeleccionado.nombre}
                  </p>
                  <p className="text-sm text-gray-600">{inscriptoSeleccionado.email}</p>
                  {inscriptoSeleccionado.asistio && (
                    <span className="inline-flex items-center gap-1 mt-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      <CheckCircle className="w-3 h-3" />
                      Asistencia confirmada
                    </span>
                  )}
                </div>

                <div>
                  <Label>Tipo de documento</Label>
                  <Select
                    value={certificadoIndividualForm.tipo}
                    onValueChange={(value) => setCertificadoIndividualForm({...certificadoIndividualForm, tipo: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="constancia">Constancia de Asistencia</SelectItem>
                      <SelectItem value="certificado">Certificado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Mensaje adicional (opcional)</Label>
                  <Textarea
                    placeholder="Ej: ¡Felicitaciones por completar el curso!..."
                    value={certificadoIndividualForm.mensaje_adicional}
                    onChange={(e) => setCertificadoIndividualForm({...certificadoIndividualForm, mensaje_adicional: e.target.value})}
                    rows={3}
                  />
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowEnviarIndividualModal(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={enviandoIndividual || !inscriptoSeleccionado.email} 
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {enviandoIndividual ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Enviar
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
